import React, { useEffect, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form as FormikForm, useField, useFormikContext } from "formik";
import { Form, Grid, Button, Label, Divider, Segment } from "semantic-ui-react";
import "./index.css";
import type { ProductData } from "./AddSupplyUnitWizard/types";
import { FamilySearchField } from "./FamilySearchField";
import { productContactOptionsWith } from "./productFields";
import { useCurrentUserName } from "./useCurrentUserName";

interface ProductFormProps {
  nextPage: () => void;
  product: ProductData;
  setProduct: (product: ProductData) => void;
  onClose: () => void;
  preselectedSupplyUnits: any[];
  // Set once the user picks a Product Contact; owned by the wizard so it
  // survives this page remounting when they come back from a later page
  contactPicked: React.MutableRefObject<boolean>;
}

const FormikField = ({
  label,
  formSubmitted,
  name,
  required,
  ...props
}: {
  label: string;
  formSubmitted: boolean;
  name: string;
  required?: boolean;
  [key: string]: any;
}) => {
  const [field, meta] = useField(name);
  return (
    <Form.Field required={required}>
      <label>{label}</label>
      <Form.Input {...field} {...props} />
      {formSubmitted && meta.error ? (
        <Label basic color="red" pointing>
          {meta.error}
        </Label>
      ) : null}
    </Form.Field>
  );
};

function AmplifyUserSync({
  currentUserName,
  contactPicked,
}: {
  currentUserName: string | undefined;
  contactPicked: React.MutableRefObject<boolean>;
}) {
  const { setFieldValue, values } = useFormikContext<any>();

  useEffect(() => {
    if (currentUserName === undefined) return;
    if (!values.createdBy) {
      setFieldValue("createdBy", currentUserName || "Unknown", false);
    }
    // New and related products default to their creator; an edit keeps the saved contact
    if (currentUserName && !values.product_id && !contactPicked.current) {
      setFieldValue("productContact", currentUserName, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserName]);

  return null;
}

const validationSchema = Yup.object({
  title: Yup.string().min(8).max(250).required("Title is required"),
  createdOn: Yup.string().required(),
  createdBy: Yup.string().required(
    "Created By could not be loaded. Please refresh and try again."
  ),
  productContact: Yup.string().required("Product Contact is required"),
});

export function ProductForm({
  nextPage,
  product,
  setProduct,
  onClose,
  preselectedSupplyUnits,
  contactPicked,
}: ProductFormProps) {
  const currentUserName = useCurrentUserName();
  const initialCreatedOn = useMemo(
    () => product.createdOn || new Date().toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Formik
      initialValues={{
        ...product,
        createdOn: initialCreatedOn,
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        setProduct({
          title: values.title,
          partNumber: values.partNumber,
          families: values.families,
          product_id: values.product_id,
          createdOn: values.createdOn,
          createdBy: values.createdBy,
          productContact: values.productContact,
        });
        setSubmitting(false);
        nextPage();
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        submitCount,
        values,
        setFieldValue,
        errors,
      }) => {
        return (
          <Form as={FormikForm} onSubmit={handleSubmit}>
            <AmplifyUserSync
              currentUserName={currentUserName}
              contactPicked={contactPicked}
            />
            <Segment basic>
              <FormikField
                label="Title"
                name="title"
                placeholder="Enter Title"
                formSubmitted={submitCount > 0}
                required
              />
              <Form.Field>
                <label>Created On</label>
                <Form.Input
                  readOnly
                  value={
                    values.createdOn
                      ? new Date(values.createdOn).toLocaleString()
                      : ""
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Created By</label>
                <Form.Input readOnly value={values.createdBy || "Loading..."} />
              </Form.Field>
              <Form.Field required>
                <label>Product Contact</label>
                <Form.Select
                  options={productContactOptionsWith(
                    values.productContact,
                    currentUserName
                  )}
                  value={values.productContact}
                  onChange={(_, data) => {
                    contactPicked.current = true;
                    setFieldValue("productContact", data.value);
                  }}
                  placeholder="Select Product Contact"
                />
                {submitCount > 0 && errors.productContact && (
                  <Label basic color="red" pointing>
                    {errors.productContact as string}
                  </Label>
                )}
              </Form.Field>
              <FamilySearchField
                families={values.families}
                onChange={(families) => setFieldValue("families", families)}
              />
            </Segment>

            <Divider />
            <Segment basic>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Button type="button" onClick={onClose} floated="left">
                      Cancel
                    </Button>
                  </Grid.Column>
                  <Grid.Column width={8} textAlign="right">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {preselectedSupplyUnits.length > 0 && (
                        <Label
                          color="grey"
                          style={{ marginRight: "10px" }}
                          size="large"
                        >
                          {`${preselectedSupplyUnits.length} supply unit(s) preselected`}
                        </Label>
                      )}
                      <Button type="submit" primary disabled={isSubmitting}>
                        Next
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Form>
        );
      }}
    </Formik>
  );
}
